import apiClient from './client';

export const marketDataApi = {
  /**
   * Effective market-data provider config for current user
   */
  getProviders: async () => {
    const response = await apiClient.get('/api/market-data/providers');
    return response.data;
  },

  /**
   * Instrument search (provider-selectable)
   * params: symbol|q, projection, assetType, source=schwab
   */
  searchInstruments: async (params = {}) => {
    const response = await apiClient.get('/api/market-data/instruments', { params });
    return response.data;
  },

  /**
   * Schwab instrument overview (fundamentals + quote) — works for mutual funds
   */
  getInstrumentOverview: async (symbol) => {
    const response = await apiClient.get(
      `/api/market-data/instruments/overview/${encodeURIComponent(symbol)}`
    );
    return response.data;
  },

  /**
   * Get market data (bars) for a symbol
   */
  getBars: async (symbol, params = {}) => {
    const response = await apiClient.get(`/api/market-data/${symbol}`, { params });
    return response.data;
  },

  /**
   * Get market data with strategy analysis
   */
  getStrategy: async (symbol, params = {}) => {
    const response = await apiClient.get(`/api/market-data/${symbol}/strategy`, { params });
    return response.data;
  },

  /**
   * Get latest quote
   */
  getQuote: async (symbol) => {
    const response = await apiClient.get(`/api/market-data/${symbol}/quote`);
    return response.data;
  },

  /**
   * Get latest trade
   */
  getTrade: async (symbol) => {
    const response = await apiClient.get(`/api/market-data/${symbol}/trade`);
    return response.data;
  },

  /**
   * Get corporate actions for one symbol or a bulk comma-separated list
   */
  getCorporateActions: async (symbolOrSymbols, params = {}) => {
    const symbols = Array.isArray(symbolOrSymbols)
      ? symbolOrSymbols.join(',')
      : String(symbolOrSymbols || '');
    if (symbols.includes(',')) {
      const response = await apiClient.get('/api/market-data/corporate-actions', {
        params: { ...params, symbols },
      });
      return response.data;
    }
    const response = await apiClient.get(`/api/market-data/${encodeURIComponent(symbols)}/corporate-actions`, {
      params,
    });
    return response.data;
  },

  /**
   * Get most active stocks
   */
  getMostActiveStocks: async (params = {}) => {
    const response = await apiClient.get('/api/market-data/screener/most-actives', { params });
    return response.data;
  },

  /**
   * Get market movers (gainers or losers)
   */
  getMarketMovers: async (params = {}) => {
    const response = await apiClient.get('/api/market-data/screener/movers', { params });
    return response.data;
  },

  /**
   * Demand/supply scanner (+10% day, 5x RVol, news, $2–$20, float < 20M)
   */
  runStockScanner: async (params = {}) => {
    const response = await apiClient.get('/api/market-data/screener/scan', {
      params,
      timeout: 60000,
    });
    return response.data;
  },

  getScannerCache: async () => {
    const response = await apiClient.get('/api/market-data/screener/cache');
    return response.data;
  },

  collectScannerData: async (body = {}) => {
    const response = await apiClient.post('/api/market-data/screener/collect', body, {
      timeout: 120000,
    });
    return response.data;
  },

  /**
   * Get asset logo URL
   */
  getLogo: async (symbol) => {
    const response = await apiClient.get(`/api/market-data/${symbol}/logo`);
    return response.data;
  },

  /**
   * Get market clock information
   */
  getMarketClock: async () => {
    const response = await apiClient.get('/api/market-data/clock');
    return response.data;
  },

  /**
   * Major index ETF levels + day change
   */
  getIndices: async (symbols = null) => {
    const params = symbols?.length ? { symbols: symbols.join(',') } : {};
    const response = await apiClient.get('/api/market-data/indices', { params });
    return response.data;
  },

  /**
   * Yahoo Finance API methods
   */
  yahoo: {
    /**
     * Get comprehensive quote summary from Yahoo Finance
     */
    getQuoteSummary: async (symbol) => {
      const response = await apiClient.get(`/api/market-data/yahoo/${symbol}/quote-summary`);
      return response.data;
    },

    /**
     * Get quote data from Yahoo Finance
     */
    getQuote: async (symbol) => {
      const response = await apiClient.get(`/api/market-data/yahoo/${symbol}/quote`);
      return response.data;
    },

    /**
     * Get historical data from Yahoo Finance
     */
    getHistorical: async (symbol, params = {}) => {
      const response = await apiClient.get(`/api/market-data/yahoo/${symbol}/historical`, { params });
      return response.data;
    },

    /**
     * Get recommendations from Yahoo Finance
     */
    getRecommendations: async (symbol) => {
      const response = await apiClient.get(`/api/market-data/yahoo/${symbol}/recommendations`);
      return response.data;
    },

    /**
     * Search for symbols using Yahoo Finance
     */
    search: async (query) => {
      const response = await apiClient.get('/api/market-data/yahoo/search', { params: { q: query } });
      return response.data;
    },

    /**
     * Get earnings calendar from Yahoo Finance
     */
    getEarningsCalendar: async (params = {}) => {
      const response = await apiClient.get('/api/market-data/yahoo/earnings-calendar', { params });
      return response.data;
    },

    /**
     * Get economic calendar from Yahoo Finance (CPI, FOMC, jobs, etc.)
     */
    getEconomicCalendar: async (params = {}) => {
      const response = await apiClient.get('/api/market-data/yahoo/economic-calendar', { params });
      return response.data;
    },

    /**
     * Get trending symbols from Yahoo Finance
     */
    getTrending: async (region = 'US') => {
      const response = await apiClient.get('/api/market-data/yahoo/trending', { params: { region } });
      return response.data;
    },
  },

  /**
   * Options overview: nearest expiry ATM IV + unusual volume
   */
  getOptionsOverview: async (symbol) => {
    const response = await apiClient.get(`/api/market-data/options/${encodeURIComponent(symbol)}/overview`);
    return response.data;
  },

  /**
   * Option chain for trading (calls/puts for an expiry)
   */
  getOptionChain: async (symbol, params = {}) => {
    const response = await apiClient.get(
      `/api/market-data/options/${encodeURIComponent(symbol)}/chain`,
      { params }
    );
    return response.data;
  },
};


